import { PageController } from '@client/components/_core/layout/PageController';
import { getConfig } from '@client/config/runtime';
import type { FileRouteTypes } from '@client/routeTree.gen';
import { useAuthStore } from '@client/stores/AuthStore';
import { loginWithRedirect } from '@client/utils/auth/auth0Client';
import { debug } from '@client/utils/logger';
import { extractPathParams } from '@client/utils/routing/extractPathParams';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { AUTH0_CONNECTIONS, LOGIN_CONNECTION_SEARCH_PARAM } from '@shared/constants/system/auth0';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import { z } from 'zod';

const connectionSchema = z.enum([AUTH0_CONNECTIONS.DATABASE.name, AUTH0_CONNECTIONS.AZURE_AD.name]);

const searchSchema = z.object({
    redirectTo: z.string().optional(),
    orgId: z.coerce.bigint().optional(),
    forceReauth: z.literal(1).optional(),
    // An unknown connection is dropped rather than failing the route: sign-in must still go ahead.
    [LOGIN_CONNECTION_SEARCH_PARAM]: connectionSchema.optional().catch(undefined),
});

export const Route = createFileRoute('/(authentication)/login')({
    validateSearch: zodValidator(searchSchema),
    component: Login,
});

function Login() {
    const { redirectTo, orgId, forceReauth, connection } = Route.useSearch();
    const authStore = useAuthStore();

    useEffect(() => {
        // redirectTo might be https://curium.app/console/123?param=value#hash — strip the query
        // and the hash before reading the organisation out of the path.
        const decodedRedirectTo = safeDecodeURIComponent(redirectTo ?? '');
        const redirectToWithoutQuery = decodedRedirectTo.split(/[?#]/)[0] ?? '';
        const params = extractPathParams(redirectToWithoutQuery, '/console/$orgId', { fuzzy: true });
        let orgIdParam: bigint | undefined;
        if (orgId) {
            orgIdParam = orgId;
        } else if (params?.orgId) {
            orgIdParam = parseOrgIdParam(params.orgId);
        } else if (decodedRedirectTo.startsWith('/admin-console' satisfies FileRouteTypes['to'])) {
            orgIdParam = SYSTEM_ORG_IDS.SYSTEM_CONSOLE;
        }

        const orgIdHint = orgIdParam?.toString();
        // Emailed links name the recipient's connection so SSO users skip the password form (CUR-1790).
        // `RequireAuth` keeps the link's query inside `redirectTo`, so read it there too.
        const connectionHint = connection ?? connectionFromRedirect(decodedRedirectTo);

        if (forceReauth === 1) {
            debug('[Login] Clearing auth token');
            authStore.clearAuthToken();
        }

        void loginWithRedirect({
            authorizationParams: {
                audience: getConfig('AUTH0_AUDIENCE', import.meta.env.VITE_AUTH0_AUDIENCE),
                scope: 'openid profile email offline_access',
                isLogin: true,
                ...(orgIdHint && { orgId: orgIdHint }),
                ...(connectionHint && { connection: connectionHint }),
            },
            appState: { redirectTo: redirectTo || '', ...(orgIdHint && { requestOrgId: orgIdHint }) },
            forceReauth: forceReauth === 1,
        });
    }, []);

    return <PageController route={Route}></PageController>;
}

function safeDecodeURIComponent(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function connectionFromRedirect(decodedRedirectTo: string) {
    // The whole query after the first `?`: a nested value may itself contain `?`.
    const path = decodedRedirectTo.split('#')[0] ?? '';
    const queryStart = path.indexOf('?');
    if (queryStart === -1) return undefined;
    const query = new URLSearchParams(path.slice(queryStart + 1));
    return connectionSchema.safeParse(query.get(LOGIN_CONNECTION_SEARCH_PARAM)).data;
}

function parseOrgIdParam(value: string): bigint | undefined {
    if (!/^-?\d+$/.test(value)) return undefined;
    return BigInt(value);
}
